import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, createBlock as _createBlock, normalizeStyle as _normalizeStyle, createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "flex gap-2 mb-2 overflow-x-auto wallet-badges-row mx-[34px]"
}
const _hoisted_2 = ["onClick"]
const _hoisted_3 = { class: "flex" }
const _hoisted_4 = {
  key: 0,
  class: "flex w-[34px] min-w-[34px] items-center justify-center cursor-pointer"
}
const _hoisted_5 = {
  key: 0,
  class: "flex w-full items-center justify-center"
}
const _hoisted_6 = {
  key: 1,
  class: "text-base"
}
const _hoisted_7 = {
  key: 0,
  class: "flex"
}
const _hoisted_8 = { class: "flex flex-col" }
const _hoisted_9 = { class: "flex flex-row items-center" }
const _hoisted_10 = {
  key: 0,
  class: "flex items-center"
}
const _hoisted_11 = { class: "font-sourcesanspro-black text-lg3" }
const _hoisted_12 = { class: "flex justify-between mb-2" }
const _hoisted_13 = { key: 0 }
const _hoisted_14 = { class: "mr-2" }
const _hoisted_15 = { class: "mr-2" }
const _hoisted_16 = { class: "flex flex-col px-3 py-2 font-sourcesanspro-regular" }
const _hoisted_17 = { class: "mb-1" }
const _hoisted_18 = { class: "flex items-center justify-between" }
const _hoisted_19 = ["src"]
const _hoisted_20 = {
  key: 1,
  class: "mastercard-mark wallet-mastercard-mark",
  "aria-hidden": "true"
}
const _hoisted_21 = { class: "wallet-button-label" }
const _hoisted_22 = {
  key: 4,
  class: "mt-3"
}
const _hoisted_23 = {
  key: 1,
  class: "flex w-[34px] min-w-[34px] items-center justify-center cursor-pointer"
}
const _hoisted_24 = {
  key: 1,
  class: "flex px-[34px] mb-3 mt-[2px] justify-end font-sourcesanspro-regular text-xs"
}
const _hoisted_25 = {
  key: 2,
  class: "flex px-[34px] mx-auto items-center justify-between w-full"
}
const _hoisted_26 = { class: "font-sourcesanspro-semibold drop-shadow-custom text-xs" }
const _hoisted_27 = ["src"]

import { computed, onMounted, onBeforeUnmount, ref, watch } from 'vue';
import router from '@/router';
import { IonButton, IonContent, IonIcon, IonItem, IonList, IonPopover, IonSpinner } from '@ionic/vue';
import { chevronDownOutline, chevronBackOutline, chevronForwardOutline, warning } from 'ionicons/icons';
import { DateTime } from 'luxon';
import { captureMessage } from '@/sentry';
import { useI18n } from 'vue-i18n';

import { useAuthStore } from '@/stores/auth/authStore';
import { Wallet } from '../interfaces/balance';
import useTenant from '@/shared/composables/useTenant';
import useToast from '@/shared/composables/useToast';
import useUtils from '@/shared/composables/useUtils';
import useWallet from '@/modules/wallet/composables/useWallet';
import useStorageService from '@/shared/composables/useStorageService';
import useUserProfile from '@/modules/profile/composables/useUserProfile';
import useQuickTopUp from '@/modules/top-up/composables/useQuickTopUp';
import QuickTopUpCard from '@/shared/components/ui/QuickTopUpCard.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'WalletBox',
  props: {
	user: { type: String, default: '' },
	walletId: { type: String, default: '' },
},
  emits: ['onWalletSet'],
  setup(__props, { expose: __expose, emit: __emit }) {

const props = __props;

// Init
const { t } = useI18n();
const emit = __emit;
const { presentToast } = useToast();
const authStore = useAuthStore();
const { getWallets, selectedWallet, getMinimumTopUpAmount } = useWallet();
const { getTenantConfigAndStyles } = useTenant();
const { tenantConfig, tenantStyles } = getTenantConfigAndStyles();
const { minimumTopUp } = getMinimumTopUpAmount(computed(() => selectedWallet.value?.id?.toString()));
const { quickPackages, fetchQuickPackages, handleQuickTopUp } = useQuickTopUp(tenantConfig, minimumTopUp);
const { refetchWalletsData, wallets, walletUpdatedAt } = getWallets();
const { convertToCurrency, toRelativeTime } = useUtils();
const unsubscribe = ref();
const isOnline = ref(navigator.onLine);
const startTime = ref(new Date());
const showWarningMessage = ref(false);
const { get, set } = useStorageService();
const { getUserProfile } = useUserProfile();
const { userProfile } = getUserProfile();
const walletUpdatedAtRelativeTime = ref();
const walletNotSynched = ref(false);
const preferredPaymentMode = computed(() => {
	if (tenantConfig.value?.viva_wallet_click_to_pay_preferred_mode) {
		return 'click_to_pay';
	}
	if (tenantConfig.value?.viva_wallet_payconiq_preferred_mode) {
		return 'payconiq';
	}
	return null;
});
const isPreferredPaymentMode = computed(() => preferredPaymentMode.value !== null);
const isPayconiqPreferredMode = computed(() => preferredPaymentMode.value === 'payconiq');
const isClickToPayPreferredMode = computed(() => preferredPaymentMode.value === 'click_to_pay');
const activeWallets = computed(() => wallets.value?.filter((w) => +w.current_balance > 0) ?? []);
const topUpButtonLabel = computed(() => {
	if (!selectedWallet.value || !isPreferredPaymentMode.value) {
		return t('wallet.add');
	}
	if (isPayconiqPreferredMode.value) {
		return `${t('wallet.add')} ${selectedWallet.value.currency.name} ${t('wallet.pay_with_payconiq')}`;
	}
	return `${t('wallet.pay_with_click_to_pay')}`;
});

// Expose the method to the parent component
__expose({
	refetchWalletsData,
});

if (authStore.isAuthenticated()) {
	refetchWalletsData();
}

const handleWalletChange = async () => {
	if (authStore.isAuthenticated() && wallets.value) {
		if (wallets.value) {
			if (wallets?.value?.length && wallets?.value?.length > 0) {
				let walletToSelect: Wallet | undefined;

				if (props.walletId) {
					walletToSelect = wallets.value?.find((wallet) => {
						return wallet.id == +props.walletId;
					});
				}

				if (!walletToSelect && selectedWallet.value) {
					walletToSelect = wallets.value.find((wallet) => wallet.id === selectedWallet.value?.id);
				}

				if (!walletToSelect) {
					const cachedWallet = await get('selectedWallet');
					if (cachedWallet) {
						walletToSelect = wallets.value.find((w) => w.id === cachedWallet.id);
					}
				}

				if (!walletToSelect) {
					walletToSelect = wallets.value?.find((wallet) => {
						return wallet.currency.default;
					});
				}

				if (!walletToSelect) {
					walletToSelect = wallets.value[0];
				}

				selectedWallet.value = walletToSelect;
			}
			emit('onWalletSet', selectedWallet.value);
			if (selectedWallet.value?.id) {
				fetchQuickPackages(selectedWallet.value.id.toString());
			}
		}
	}
};

const calculateWalletNotSynched = async () => {
	const now = DateTime.now();
	const walletUpdatedAtDate = DateTime.fromMillis(walletUpdatedAt.value);
	const diffInMilliseconds = now.diff(walletUpdatedAtDate).milliseconds;

	if (diffInMilliseconds > 1000 * 60 * 1) {
		walletNotSynched.value = true;
	} else {
		walletNotSynched.value = false;
	}
};

const calculateRelativeTime = async () => {
	walletUpdatedAt.value = await get('walletUpdatedAt');
	if (walletUpdatedAt.value) {
		walletUpdatedAtRelativeTime.value = toRelativeTime(t, walletUpdatedAt.value);
		calculateWalletNotSynched();
	}
};

onMounted(async () => {
	await handleWalletChange();
	await calculateRelativeTime();
});

watch(wallets, async () => {
	if (wallets.value) {
		await handleWalletChange();
		await calculateRelativeTime();
	}
});

watch(walletUpdatedAt, async () => {
	await calculateRelativeTime();
});

setInterval(async () => {
	await calculateRelativeTime();
}, 15 * 1000);

const warningMessageInterval = setInterval(() => {
	const seconds = Math.round(DateTime.now().diff(DateTime.fromJSDate(startTime.value)).milliseconds / 1000);
	if (seconds > 5 && !selectedWallet.value) {
		showWarningMessage.value = true;
		clearInterval(warningMessageInterval);
	} else if (seconds > 5) {
		clearInterval(warningMessageInterval);
	} else {
		showWarningMessage.value = false;
	}
}, 1000);

onBeforeUnmount(() => {
	if (unsubscribe.value) {
		unsubscribe.value();
	}
});

const selectWallet = async (wallet: Wallet | undefined) => {
	if (!wallet) {
		captureMessage(`Attempted to select a wallet that does not exist`);
	}
	selectedWallet.value = wallet;
	await set('selectedWallet', wallet);
	emit('onWalletSet', wallet);
};

const getWalletAtIndex = (index: number) => {
	const availableWallets = wallets.value ?? [];

	if (!availableWallets.length) {
		return undefined;
	}

	if (index >= availableWallets.length) {
		index = 0;
	} else if (index < 0) {
		index = availableWallets.length - 1;
	}
	return availableWallets[index];
};

const currentWalletIndex = () => {
	if (!wallets.value || !selectedWallet.value) {
		return 0;
	}

	return wallets.value.findIndex((wallet) => wallet.id === selectedWallet.value?.id);
};

const getPreviousWallet = () => {
	return getWalletAtIndex(currentWalletIndex() - 1);
};

const getNextWallet = () => {
	return getWalletAtIndex(currentWalletIndex() + 1);
};

const selectPreviousWallet = () => {
	selectWallet(getPreviousWallet());
};

const selectNextWallet = () => {
	selectWallet(getNextWallet());
};

const hideOfflineMessage = () => {
	isOnline.value = true;
};

const showOfflineMessage = () => {
	isOnline.value = false;
};

window.addEventListener('offline', () => {
	showOfflineMessage();
});

window.addEventListener('online', () => {
	hideOfflineMessage();
});

const goTo = async (link: string) => {
	// If the user is not authenticated, we will first ask him to sign in
	if (!authStore.isAuthenticated()) {
		await presentToast('top', t('general.please_login_first'), 5000, 'danger');
		await router.push('/auth/signuphome');
	} else {
		if (selectedWallet.value?.currency) {
			await router.push(link);
		} else {
			await presentToast('top', t('error.offline'), 5000, 'danger');
		}
	}
};

const onQuickTopUp = (walletPackage) => {
	handleQuickTopUp(walletPackage);
};

const walletLayoutImage = ref(userProfile?.value?.wallet_layout_image ?? null);
const walletLayoutTextColor = ref(userProfile?.value?.wallet_layout_text_color ?? null);

watch(
	() => userProfile?.value,
	(newValue) => {
		walletLayoutImage.value = newValue?.wallet_layout_image ?? null;
		walletLayoutTextColor.value = newValue?.wallet_layout_text_color ?? null;
	},
	{ deep: true },
);

return (_ctx: any,_cache: any) => {
  return (!_unref(tenantConfig)?.hide_wallet)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        class: _normalizeClass([
			{
				'mb-2': !isPreferredPaymentMode.value,
				'mb-8': isPreferredPaymentMode.value,
			},
		])
      }, [
        (isOnline.value && _unref(authStore).isAuthenticated() && activeWallets.value.length > 1)
          ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(activeWallets.value, (wallet) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: wallet.id,
                  class: _normalizeClass(["wallet-badge flex-shrink-0 cursor-pointer", { 'wallet-badge--active': _unref(selectedWallet)?.id === wallet.id }]),
                  onClick: ($event: any) => (selectWallet(wallet))
                }, _toDisplayString(wallet.currency.prefix) + " " + _toDisplayString(_unref(convertToCurrency)(+wallet.current_balance)), 11, _hoisted_2))
              }), 128))
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_3, [
          (isOnline.value && _unref(authStore).isAuthenticated())
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                (_unref(wallets) && _unref(wallets)?.length > 1)
                  ? (_openBlock(), _createBlock(_unref(IonIcon), {
                      key: 0,
                      icon: _unref(chevronBackOutline),
                      class: "text-white active:bg-white/30 active:rounded-full active:p-1",
                      onClick: _cache[0] || (_cache[0] = ($event: any) => (selectPreviousWallet()))
                    }, null, 8, ["icon"]))
                  : _createCommentVNode("", true)
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", {
            class: _normalizeClass([[
					{
						'bg-white-image': !isPreferredPaymentMode.value,
						'payconiq': isPayconiqPreferredMode.value,
						'click-to-pay-wallet': isClickToPayPreferredMode.value,
						'text-white': isPreferredPaymentMode.value,
						'font-color-dark': !isPreferredPaymentMode.value,
						'mx-[34px]': !isOnline.value || !_unref(authStore).isAuthenticated(),
					},
				], "flex flex-col justify-between rounded-md w-full"]),
            style: _normalizeStyle({
					'background-image': !isPreferredPaymentMode.value && walletLayoutImage.value ? `url(${walletLayoutImage.value})` : undefined,
					'background-size': 'cover',
					'color': !isPreferredPaymentMode.value && walletLayoutImage.value && walletLayoutTextColor.value ? walletLayoutTextColor.value + ' !important' : undefined,
				})
          }, [
            _createElementVNode("div", {
              class: _normalizeClass(["flex flex-col justify-between w-full p-4", [
						{
							'px-4': !_unref(authStore).isAuthenticated(),
						},
					]])
            }, [
              (isOnline.value && _unref(authStore).isAuthenticated() && !_unref(selectedWallet))
                ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                    _createVNode(_unref(IonSpinner), {
                      name: "dots",
                      class: _normalizeClass([
								{
									'text-anykrowd-dark': !walletLayoutImage.value && !isPreferredPaymentMode.value,
								},
							]),
                      style: _normalizeStyle({
								'color': !isPreferredPaymentMode.value && walletLayoutImage.value && walletLayoutTextColor.value ? walletLayoutTextColor.value + ' !important' : undefined,
							})
                    }, null, 8, ["class", "style"])
                  ]))
                : _createCommentVNode("", true),
              (_unref(authStore).isAuthenticated() && showWarningMessage.value && !_unref(selectedWallet))
                ? (_openBlock(), _createElementBlock("div", _hoisted_6, _toDisplayString(_unref(t)('general.connection.offline_long_message')), 1))
                : _createCommentVNode("", true),
              (_unref(authStore).isAuthenticated())
                ? (_openBlock(), _createElementBlock("div", {
                    key: 2,
                    class: _normalizeClass(["flex flex-row justify-between", { 'mb-6': !_unref(selectedWallet)?.currency?.description }])
                  }, [
                    (_unref(selectedWallet))
                      ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                          _createElementVNode("div", _hoisted_8, [
                            _createElementVNode("div", {
                              class: _normalizeClass(["font-sourcesanspro-semibold text-sm", [
										{
											'text-anykrowd-dark/50': !walletLayoutImage.value && !isPreferredPaymentMode.value,
										},
									]])
                            }, _toDisplayString(_unref(t)('components.wallet_box.your_balance', { currency: _unref(selectedWallet).currency.name })), 3),
                            _createElementVNode("div", _hoisted_9, [
                              (_unref(wallets) && _unref(wallets).length > 0 && (walletNotSynched.value || !isOnline.value))
                                ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                                    _createVNode(_unref(IonIcon), {
                                      id: "popover-wallet-alert",
                                      size: "medium",
                                      icon: _unref(warning),
                                      class: "mr-1",
                                      style: _normalizeStyle(`color: ${_unref(tenantStyles)?.tertiary_color}`)
                                    }, null, 8, ["icon", "style"]),
                                    _createVNode(_unref(IonPopover), {
                                      trigger: "popover-wallet-alert",
                                      "dismiss-on-select": true
                                    }, {
                                      default: _withCtx(() => [
                                        _createVNode(_unref(IonContent), { class: "ion-padding bg-black" }, {
                                          default: _withCtx(() => [
                                            _createTextVNode(_toDisplayString(_unref(t)('wallet.not_synched')), 1)
                                          ]),
                                          _: 1
                                        })
                                      ]),
                                      _: 1
                                    })
                                  ]))
                                : _createCommentVNode("", true),
                              _createElementVNode("span", _hoisted_11, _toDisplayString(_unref(selectedWallet).currency.prefix) + " " + _toDisplayString(_unref(convertToCurrency)(+_unref(selectedWallet).current_balance)), 1)
                            ])
                          ])
                        ]))
                      : _createCommentVNode("", true),
                    _createElementVNode("div", _hoisted_12, [
                      _cache[3] || (_cache[3] = _createElementVNode("div", null, null, -1)),
                      (_unref(selectedWallet))
                        ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                            _createElementVNode("div", {
                              id: "popover-button",
                              class: _normalizeClass(["flex items-center justify-center rounded-lg font-sourcesanspro-bold text-md cursor-pointer", [
										{
											'text-anykrowd-dark': !walletLayoutImage.value && !isPreferredPaymentMode.value,
										},
									]])
                            }, [
                              _createElementVNode("div", _hoisted_14, _toDisplayString(_unref(selectedWallet).currency.prefix), 1),
                              _createElementVNode("div", _hoisted_15, _toDisplayString(_unref(selectedWallet)?.currency.name), 1),
                              (_unref(wallets) && _unref(wallets)?.length > 1)
                                ? (_openBlock(), _createBlock(_unref(IonIcon), {
                                    key: 0,
                                    icon: _unref(chevronDownOutline),
                                    class: _normalizeClass(["text-md", [
											{
												'text-anykrowd-dark': !walletLayoutImage.value && !isPreferredPaymentMode.value,
											},
										]]),
                                    style: _normalizeStyle({
											'color': !isPreferredPaymentMode.value && walletLayoutImage.value && walletLayoutTextColor.value ? walletLayoutTextColor.value + ' !important' : undefined,
										})
                                  }, null, 8, ["icon", "class", "style"]))
                                : _createCommentVNode("", true)
                            ], 2),
                            _createVNode(_unref(IonPopover), {
                              trigger: "popover-button",
                              "dismiss-on-select": true
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_unref(IonContent), null, {
                                  default: _withCtx(() => [
                                    _createVNode(_unref(IonList), null, {
                                      default: _withCtx(() => [
                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(wallets), (wallet) => {
                                          return (_openBlock(), _createBlock(_unref(IonItem), {
                                            class: "flex items-center justify-center ion-no-padding ion-no-margin",
                                            key: wallet.id,
                                            button: true,
                                            onClick: ($event: any) => (selectWallet(wallet)),
                                            detail: false
                                          }, {
                                            default: _withCtx(() => [
                                              _createElementVNode("div", _hoisted_16, [
                                                _createElementVNode("div", _hoisted_17, [
                                                  _createElementVNode("span", null, _toDisplayString(wallet.currency.prefix) + " " + _toDisplayString(wallet.current_balance) + " " + _toDisplayString(wallet.currency.name), 1)
                                                ])
                                              ])
                                            ]),
                                            _: 2
                                          }, 1032, ["onClick"]))
                                        }), 128))
                                      ]),
                                      _: 1
                                    })
                                  ]),
                                  _: 1
                                })
                              ]),
                              _: 1
                            })
                          ]))
                        : _createCommentVNode("", true)
                    ])
                  ], 2))
                : _createCommentVNode("", true),
              (_unref(authStore).isAuthenticated() && _unref(selectedWallet) && _unref(selectedWallet).currency?.description)
                ? (_openBlock(), _createElementBlock("div", {
                    key: 3,
                    class: _normalizeClass(["flex flex-col font-sourcesanspro-semibold text-sm mb-6 mt-4 break-words", [
							{
								'text-anykrowd-dark/50': !walletLayoutImage.value && !isPreferredPaymentMode.value,
							},
						]])
                  }, _toDisplayString(_unref(selectedWallet).currency?.description), 3))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_18, [
                (isOnline.value && _unref(authStore).isAuthenticated() && _unref(selectedWallet) && _unref(selectedWallet).currency.topupable)
                  ? (_openBlock(), _createBlock(_unref(IonButton), {
                      key: 0,
                      class: _normalizeClass([[
								{
									'black': !isPreferredPaymentMode.value,
									'preferred-wallet-button': isPreferredPaymentMode.value,
									'click-to-pay-wallet-button': isClickToPayPreferredMode.value,
								},
							], "w-full h-[40px] font-sourcesanspro-semibold text-sm2 ion-no-margin"]),
                      shape: !isPreferredPaymentMode.value ? 'round' : undefined,
                      onClick: _cache[1] || (_cache[1] = ($event: any) => (goTo(`/top-up/${_unref(selectedWallet)!.id}`)))
                    }, {
                      default: _withCtx(() => [
                        (isPayconiqPreferredMode.value)
                          ? (_openBlock(), _createElementBlock("img", {
                              key: 0,
                              src: require('@/statics/png/payconiq/payconiq-horizontal.png'),
                              class: "mr-1"
                            }, null, 8, _hoisted_19))
                          : _createCommentVNode("", true),
                        (isClickToPayPreferredMode.value)
                          ? (_openBlock(), _createElementBlock("span", _hoisted_20, [...(_cache[4] || (_cache[4] = [
                              _createElementVNode("span", null, null, -1),
                              _createElementVNode("span", null, null, -1)
                            ]))]))
                          : _createCommentVNode("", true),
                        _createElementVNode("span", _hoisted_21, _toDisplayString(topUpButtonLabel.value), 1)
                      ]),
                      _: 1
                    }, 8, ["class", "shape"]))
                  : _createCommentVNode("", true)
              ]),
              (isOnline.value && _unref(authStore).isAuthenticated() && _unref(quickPackages).length > 0)
                ? (_openBlock(), _createElementBlock("div", _hoisted_22, [
                    _createVNode(QuickTopUpCard, {
                      "quick-packages": _unref(quickPackages),
                      onOnQuickTopUp: onQuickTopUp
                    }, null, 8, ["quick-packages"])
                  ]))
                : _createCommentVNode("", true)
            ], 2)
          ], 6),
          (isOnline.value && _unref(authStore).isAuthenticated())
            ? (_openBlock(), _createElementBlock("div", _hoisted_23, [
                (_unref(wallets) && _unref(wallets)?.length > 1)
                  ? (_openBlock(), _createBlock(_unref(IonIcon), {
                      key: 0,
                      icon: _unref(chevronForwardOutline),
                      class: "text-white active:bg-white/30 active:rounded-full active:p-1",
                      onClick: _cache[2] || (_cache[2] = ($event: any) => (selectNextWallet()))
                    }, null, 8, ["icon"]))
                  : _createCommentVNode("", true)
              ]))
            : _createCommentVNode("", true)
        ]),
        (isOnline.value && walletUpdatedAtRelativeTime.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_24, _toDisplayString(_unref(t)('wallet.updated_at')) + " " + _toDisplayString(walletUpdatedAtRelativeTime.value), 1))
          : _createCommentVNode("", true),
        (isOnline.value && isPayconiqPreferredMode.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_25, [
              _createElementVNode("span", _hoisted_26, _toDisplayString(_unref(t)('components.powered_by_payconiq')), 1),
              _createElementVNode("img", {
                src: require('@/statics/svg/payconiq/payconiq-horizontal-neg.svg'),
                class: "w-[74px] h-[13px] drop-shadow-custom"
              }, null, 8, _hoisted_27)
            ]))
          : _createCommentVNode("", true)
      ], 2))
    : _createCommentVNode("", true)
}
}

})